export interface BackendConfig {
  protocol: 'http' | 'https';
  host: string;
  port: number;
  path: string;
  apiKey: string;  // X-API-Key for UniGateway authentication
  developerMode: boolean;
  /** Symbol faktury obcej (np. fvo, fok) – używany w Weryfikacja faktury obcej (proc 26771) */
  foreignInvoiceSymbol: string;
  /** Czy żądania API idą przez proxy na tym samym serwerze (unika CORS). Wyłącz dla połączenia bezpośredniego z backendem. */
  useProxy: boolean;
  /** Nazwa drukarki etykiet w CUPS (np. datamax, datamax2) */
  labelPrinterName: string;
}

/**
 * Backend z env builda (VITE_DEFAULT_* w .env.<tryb> instalacji). Rezerwa na brak
 * zmiennej (backend ForClever — build VPS 192.168.1.194) siedzi w vite.config.ts, nie
 * tutaj: build instalacji on-prem nie może nieść klucza API innego klienta. Instalacje
 * on-prem mają pusty klucz — realny przychodzi w seedzie z bramy/pulpitu.
 */
const DEFAULT_CONFIG: BackendConfig = {
  protocol: import.meta.env.VITE_DEFAULT_PROTOCOL === 'http' ? 'http' : 'https',
  developerMode: false,
  host: import.meta.env.VITE_DEFAULT_HOST,
  port: Number(import.meta.env.VITE_DEFAULT_PORT),
  path: import.meta.env.VITE_DEFAULT_PATH,
  apiKey: import.meta.env.VITE_DEFAULT_API_KEY,
  foreignInvoiceSymbol: 'fvo',
  useProxy: import.meta.env.VITE_USE_PROXY_GATEWAY === 'true',
  labelPrinterName: 'datamax',
};

const STORAGE_KEY = 'kioskfirm_backend_config';

class ConfigService {
  /**
   * Migawka konfiguracji TEJ karty. localStorage jest wspólny dla całego
   * originu — dwie karty pulpitu (prod + preprod na jednym hoście) seedują ten
   * sam STORAGE_KEY i nadpisują się nawzajem. Odczyt z localStorage per żądanie
   * mieszał wtedy parę {baseUrl, apiKey} między instalacjami → 401 na
   * unigateway (ZKS, 2026-08-21). Dlatego localStorage czytamy RAZ przy
   * starcie karty; zapis z okienka ustawień aktualizuje migawkę i localStorage.
   */
  private activeConfig: BackendConfig | null = null;

  /**
   * Get current backend configuration (per-tab snapshot; seeded from
   * localStorage on first read) or return default.
   */
  getConfig(): BackendConfig {
    if (!this.activeConfig) {
      this.activeConfig = this.readStoredConfig();
    }
    return { ...this.activeConfig };
  }

  private readStoredConfig(): BackendConfig {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const host = parsed.host != null ? String(parsed.host).trim() : '';
        if (parsed.protocol && host !== '' && parsed.port != null && parsed.path) {
          return { ...DEFAULT_CONFIG, ...parsed };
        }
      }
    } catch (error) {
      console.error('[ConfigService] Error loading config:', error);
    }
    return { ...DEFAULT_CONFIG };
  }

  /**
   * Konfiguracja podana przez hosta (pulpit) przez postMessage — ustawia migawkę TEJ
   * karty i CELOWO nie dotyka localStorage: klucz jest wspólny dla originu, więc zapis
   * nadpisałby konfigurację sąsiedniej karty (drugiej instancji Firmatic). Trwałość
   * między przeładowaniami zapewnia sessionStorage w host-seed.ts, który jest per karta.
   */
  applyTabSeed(partial: Partial<BackendConfig>): void {
    const base = this.activeConfig ?? this.readStoredConfig();
    this.activeConfig = { ...base, ...partial };
  }

  /**
   * Save backend configuration to the tab snapshot and localStorage
   */
  saveConfig(config: BackendConfig): void {
    try {
      // Trim whitespace from string fields
      const cleanConfig: BackendConfig = {
        ...config,
        host: config.host.trim(),
        path: config.path.trim(),
        apiKey: config.apiKey.trim(),
        foreignInvoiceSymbol: (config.foreignInvoiceSymbol ?? '').trim(),
        labelPrinterName: (config.labelPrinterName ?? '').trim(),
      };
      this.activeConfig = cleanConfig;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanConfig));
      console.log('[ConfigService] Config saved:', cleanConfig);
    } catch (error) {
      console.error('[ConfigService] Error saving config:', error);
    }
  }

  /**
   * Reset configuration to default values
   */
  resetToDefault(): void {
    this.activeConfig = { ...DEFAULT_CONFIG };
    localStorage.removeItem(STORAGE_KEY);
    console.log('[ConfigService] Config reset to default');
  }

  /**
   * Get full base URL from current configuration
   */
  getBaseUrl(): string {
    return this.getBaseUrlFromConfig(this.getConfig());
  }

  /**
   * Tryb proxy: żądania idą na ten sam origin (gdzie działa proxy), a serwer przekierowuje
   * na backend z panelu (nagłówek X-Backend-Target). Ustawiane per-użytkownik w panelu ustawień.
   */
  isProxyGatewayMode(): boolean {
    return this.getConfig().useProxy === true && typeof window !== 'undefined';
  }

  /**
   * Wartość dla nagłówka X-Backend-Target (tylko gdy isProxyGatewayMode).
   * Gdy host pusty – zwraca '' (backend wybierany wyłącznie w okienku ustawień).
   */
  getBackendTargetForHeader(): string {
    const cfg = this.getConfig();
    const host = cfg.host.trim();
    if (!host) return '';
    return `${cfg.protocol}://${host}:${cfg.port}`;
  }

  /**
   * Get full base URL from provided configuration.
   * In dev, use local proxy for fc.firmatic.pl to avoid CORS.
   * When proxy gateway: same origin + path; backend from X-Backend-Target.
   * Gdy host pusty – zwraca '' (użytkownik musi wpisać backend w ustawieniach).
   */
  getBaseUrlFromConfig(cfg: BackendConfig): string {
    const host = cfg.host.trim();
    const isBrowser = typeof window !== 'undefined';
    const cfgProxy = cfg.useProxy === true && isBrowser;

    if (!host) {
      if (cfgProxy) {
        return `${window.location.origin}${(cfg.path || '').trim() || '/firmbffservice'}`;
      }
      return '';
    }
    const path = cfg.path.trim();
    const baseUrl = `${cfg.protocol}://${host}:${cfg.port}${path}`;

    const isDev = import.meta.env.DEV;
    const useFcProxy = isDev
      && isBrowser
      && cfg.protocol === 'http'
      && host === 'fc.firmatic.pl'
      && cfg.port === 8088
      && path === '/firmbffservice';

    if (useFcProxy) {
      return `${window.location.origin}/bff`;
    }

    if (cfgProxy) {
      return `${window.location.origin}${path}`;
    }

    return baseUrl;
  }

  /**
   * Get default configuration
   */
  getDefaultConfig(): BackendConfig {
    return { ...DEFAULT_CONFIG };
  }

  /**
   * Get API key from config
   */
  getApiKey(): string {
    return this.getConfig().apiKey || '';
  }

  /**
   * Test connection to backend by calling /health endpoint.
   * Uses same URL logic as API calls (incl. dev proxy) to avoid CORS issues.
   */
  async testConnection(config?: BackendConfig): Promise<{ success: boolean; message: string; responseTime?: number }> {
    const cfg = config || this.getConfig();
    const baseUrl = this.getBaseUrlFromConfig(cfg);
    const url = `${baseUrl.replace(/\/$/, '')}/health`;

    const headers: Record<string, string> = {};
    const apiKey = (cfg.apiKey ?? '').trim();
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }
    const cfgProxy = cfg.useProxy === true && typeof window !== 'undefined';
    if (cfgProxy) {
      const host = cfg.host.trim();
      if (host) {
        headers['X-Backend-Target'] = `${cfg.protocol}://${host}:${cfg.port}`;
      }
    }

    try {
      const startTime = Date.now();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        headers
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          message: `Połączenie OK (${responseTime}ms)`,
          responseTime
        };
      }

      return {
        success: false,
        message: `HTTP ${response.status}: ${response.statusText}`
      };
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { success: false, message: 'Timeout (5s) - brak odpowiedzi' };
      }

      // Detailed error messages for common issues
      const errorMsg = error.message || 'Błąd połączenia';
      let hint = '';

      if (errorMsg.includes('Failed to fetch')) {
        if (cfg.protocol === 'https') {
          hint = ' (sprawdź certyfikat SSL lub użyj HTTP)';
        } else {
          // Gdy curl działa, a przeglądarka zwraca "Failed to fetch" → CORS
          hint = ' (jeśli curl działa – problem CORS: skonfiguruj UniGateway: Access-Control-Allow-Origin)';
        }
      } else if (errorMsg.includes('NetworkError') || errorMsg.includes('net::')) {
        hint = ' (problem z siecią lub CORS)';
      }

      return {
        success: false,
        message: errorMsg + hint
      };
    }
  }

  /**
   * Check if developer mode is enabled
   */
  isDeveloperMode(): boolean {
    return this.getConfig().developerMode === true;
  }

  /**
   * Toggle developer mode
   */
  setDeveloperMode(enabled: boolean): void {
    const config = this.getConfig();
    config.developerMode = enabled;
    this.saveConfig(config);
    console.log('[ConfigService] Developer mode:', enabled);
  }

  /**
   * Validate backend configuration
   */
  validateConfig(config: BackendConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.protocol || !['http', 'https'].includes(config.protocol)) {
      errors.push('Protokół musi być http lub https');
    }

    if (!config.host || config.host.trim() === '') {
      errors.push('Host nie może być pusty');
    }

    if (!config.port || config.port < 1 || config.port > 65535) {
      errors.push('Port musi być z zakresu 1-65535');
    }

    if (!config.path || !config.path.startsWith('/')) {
      errors.push('Ścieżka musi zaczynać się od /');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

export const configService = new ConfigService();


